'use client';

import React, { useState, useMemo } from 'react';
import {
  Search,
  Eye,
  Banknote,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Paperclip,
  ChevronLeft,
  ChevronRight,
  Download,
} from 'lucide-react';
import { useToast } from '@/components/ui/Toast';
import { fundRequests, formatTZS, type FundRequest } from '@/lib/mockData';

type SortKey = keyof FundRequest | null;
type SortDir = 'asc' | 'desc';
const PAGE_SIZE_OPTIONS = [5, 10, 20];

export default function ReleasedRequestsContent() {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('releaseDate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const rows = fundRequests.filter((r) => r.status === 'Released');

  const filtered = useMemo(() => {
    let data = rows;
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.requestId.toLowerCase().includes(q) ||
          r.requestorFirstName.toLowerCase().includes(q) ||
          r.requestorLastName.toLowerCase().includes(q) ||
          r.clientName.toLowerCase().includes(q) ||
          r.bankReference.toLowerCase().includes(q)
      );
    }
    if (sortKey) {
      data = [...data].sort((a, b) => {
        const av = a[sortKey] ?? '';
        const bv = b[sortKey] ?? '';
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return data;
  }, [rows, search, sortKey, sortDir]);

  const totalReleased = filtered.reduce((s, r) => s + r.amountRequested, 0);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="text-primary" />
      : <ChevronDown size={12} className="text-primary" />;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-700 text-foreground">Released Requests</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Audit trail of all disbursed fund requests
          </p>
        </div>
        <button
          onClick={() => toast('info', 'Export started', 'Generating CSV export of released requests…')}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm font-600 text-foreground hover:bg-muted transition-all duration-150 active:scale-95"
        >
          <Download size={15} />
          Export CSV
        </button>
      </div>

      {/* Summary bar */}
      <div className="bg-info-bg border border-info/20 rounded-xl px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Banknote size={16} className="text-info" />
          <span className="text-sm font-600 text-foreground">
            {filtered.length} funds released — total{' '}
            <span className="text-info tabular-nums">{formatTZS(totalReleased)}</span>
          </span>
        </div>
        <span className="text-xs text-muted-foreground">Read-only audit trail</span>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-2">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by ID, requestor, bank ref…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-8 pr-3 py-2 text-sm border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-muted-foreground font-500">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-input rounded-md px-2 py-1.5 text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <label className="text-xs text-muted-foreground font-500">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-input rounded-md px-2 py-1.5 text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {([
                  ['requestId', 'Request ID'],
                  ['requestorFirstName', 'Requestor'],
                  ['clientName', 'Client Name'],
                  ['customerProject', 'Project'],
                  ['expenseType', 'Expense Type'],
                  ['amountRequested', 'Amount (TZS)'],
                  ['bankReference', 'Bank Reference'],
                  ['attachmentCount', 'Files'],
                  ['releaseDate', 'Release Date'],
                ] as [SortKey, string][]).map(([key, label]) => (
                  <th
                    key={`th-rel-${key}`}
                    className="text-left px-4 py-3 text-[11px] font-600 uppercase tracking-widest text-muted-foreground whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => handleSort(key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {label}
                      <SortIcon col={key} />
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3 text-[11px] font-600 uppercase tracking-widest text-muted-foreground text-right whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center">
                    <Banknote size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-sm font-600 text-foreground">No released requests</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      No funds have been released yet. Released requests will appear here as an audit trail.
                    </p>
                  </td>
                </tr>
              ) : (
                paginated.map((req, idx) => (
                  <tr
                    key={req.id}
                    className={`border-b border-border last:border-0 hover:bg-muted/40 transition-colors duration-100 ${
                      idx % 2 === 1 ? 'bg-muted/20' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-600 text-primary tabular-nums text-xs whitespace-nowrap">
                      {req.requestId}
                    </td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">
                      {req.requestorFirstName} {req.requestorLastName}
                    </td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap max-w-[140px] truncate">
                      {req.clientName}
                    </td>
                    <td className="px-4 py-3 text-foreground max-w-[160px] truncate">
                      {req.customerProject}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {req.expenseType}
                    </td>
                    <td className="px-4 py-3 text-right font-600 tabular-nums text-foreground whitespace-nowrap">
                      {formatTZS(req.amountRequested)}
                    </td>
                    <td className="px-4 py-3 text-xs font-500 text-foreground tabular-nums whitespace-nowrap">
                      {req.bankReference || <span className="text-muted-foreground/40">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {req.attachmentCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Paperclip size={12} />
                          {req.attachmentCount}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground font-500 tabular-nums whitespace-nowrap">
                      {req.releaseDate ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          title="View full request details"
                          className="p-1.5 rounded-md text-muted-foreground hover:text-info hover:bg-info/10 transition-all duration-150"
                        >
                          <Eye size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border bg-muted/20">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Show</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-input rounded-md px-2 py-1 text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={`ps-rel-${s}`} value={s}>{s}</option>
              ))}
            </select>
            <span>of {filtered.length} released</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={`page-rel-${i + 1}`}
                onClick={() => setPage(i + 1)}
                className={`w-7 h-7 rounded-md text-xs font-600 transition-all duration-150 ${
                  page === i + 1
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}